import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../AppContext';
import { useTranslation } from 'react-i18next';
import BlockedUsersView from './BlockedUsersView';

const PrivacyView = () => {
    const { t } = useTranslation();
    const { currentUserProfile, updateUserProfile, appFunctions, setMessage } = useAppContext();
    const { httpsCallable } = useAppContext();
    const [privacySettings, setPrivacySettings] = useState({
        profileVisibility: 'public',
        activityVisibility: 'public',
        messagePolicy: 'everyone',
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        if (currentUserProfile?.privacySettings) {
            setPrivacySettings(currentUserProfile.privacySettings);
        }
        setIsLoading(false);
    }, [currentUserProfile]);

    const handleSettingChange = (setting, value) => {
        const newSettings = {
            ...privacySettings,
            [setting]: value,
        };
        setPrivacySettings(newSettings);
        updateUserProfile(currentUserProfile.id, { privacySettings: newSettings });
    };

    const handleExportData = async () => {
        setIsExporting(true);
        try {
            const requestExport = httpsCallable(appFunctions, 'requestUserDataExport');
            await requestExport();
            setMessage(t('settings.privacy.exportSuccess'));
        } catch (error) {
            setMessage(t('settings.privacy.exportError', { message: error.message }));
        } finally {
            setIsExporting(false);
        }
    };

    if (isLoading) {
        return <div>{t('loading')}</div>;
    }

    return (
        <div className="bg-black/20 backdrop-blur-md rounded-2xl p-6 border border-slate-700/50">
            <h2 className="text-3xl font-bold font-playfair mb-2 text-green-300">{t('settings.privacy.title')}</h2>
            <p className="mb-6 text-gray-400">{t('settings.privacy.description')}</p>

            <div className="space-y-6">
                {/* Profile Visibility */}
                <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                    <h3 className="font-semibold text-lg mb-2">{t('settings.privacy.profileVisibilityTitle')}</h3>
                    <p className="text-sm text-gray-400 mb-3">{t('settings.privacy.profileVisibilityDescription')}</p>
                    <div className="flex space-x-4">
                        <button
                            onClick={() => handleSettingChange('profileVisibility', 'public')}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${privacySettings.profileVisibility === 'public' ? 'bg-green-600 text-white' : 'bg-gray-600 hover:bg-gray-500'}`}
                        >
                            {t('settings.privacy.public')}
                        </button>
                        <button
                            onClick={() => handleSettingChange('profileVisibility', 'private')}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${privacySettings.profileVisibility === 'private' ? 'bg-green-600 text-white' : 'bg-gray-600 hover:bg-gray-500'}`}
                        >
                            {t('settings.privacy.private')}
                        </button>
                    </div>
                </div>

                {/* Message Policy */}
                <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                    <h3 className="font-semibold text-lg mb-2">{t('settings.privacy.messagePolicyTitle')}</h3>
                    <p className="text-sm text-gray-400 mb-3">{t('settings.privacy.messagePolicyDescription')}</p>
                    <div className="flex space-x-4">
                         <button
                            onClick={() => handleSettingChange('messagePolicy', 'everyone')}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${privacySettings.messagePolicy === 'everyone' ? 'bg-green-600 text-white' : 'bg-gray-600 hover:bg-gray-500'}`}
                        >
                            {t('settings.privacy.everyone')}
                        </button>
                        <button
                            onClick={() => handleSettingChange('messagePolicy', 'connections')}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${privacySettings.messagePolicy === 'connections' ? 'bg-green-600 text-white' : 'bg-gray-600 hover:bg-gray-500'}`}
                        >
                            {t('settings.privacy.connectionsOnly')}
                        </button>
                    </div>
                </div>

                {/* Data Export */}
                <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                    <h3 className="font-semibold text-lg mb-2">{t('settings.privacy.exportTitle')}</h3>
                    <p className="text-sm text-gray-400 mb-3">{t('settings.privacy.exportDescription')}</p>
                    <button
                        onClick={handleExportData}
                        disabled={isExporting}
                        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-lg transition duration-300 disabled:opacity-50"
                    >
                        {isExporting ? t('settings.privacy.exporting') : t('settings.privacy.exportButton')}
                    </button>
                </div>

                {/* Blocked Users */}
                <BlockedUsersView />
            </div>
        </div>
    );
};


export default PrivacyView;